/**
 * dview:TopologyViewer: the net elements the object sits on, and the net elements one relation
 * away (columns next-a and next-b). Each own element is a row with its neighbours on either side.
 * An element is named by the track it belongs to (column owner), with its id as small text.
 */

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { uriToDereferencePath } from '@utils/prefix.utils';
import { pickLang, settingList } from '@utils/viewShapes.utils';
import { recordName } from './values';
import type { Widget } from './types';
import styles from './topology.module.scss';

const TRACK = 'http://ontology.railml.org/railml3#Track';
const NODE_GAP = 40;
const ROW_GAP = 28;

/** _netelement_a1abfa44-b7a4-…_0.67271668 → "a1abfa44 · 0.67" */
export function elementName(iri: string): string {
  const match = /_netelement_([0-9a-f]{8})[0-9a-f-]*_([0-9.]+)$/.exec(iri);
  if (!match) return iri.split(/[#/]/).pop() ?? iri;
  return `${match[1]} · ${Number(match[2]).toFixed(2)}`;
}

/**
 * The track among the objects with a linear location on a net element (kg-gen names tracks
 * _track_<designator>, other linear objects only start with _track_).
 */
export const trackAmong = (owners: { value: string }[] | undefined): string | null =>
  (owners ?? []).map((value) => value.value).find((iri) => /\/_track_[A-Z]+-/.test(iri)) ?? null;

const Topology = ({ property, context }: Parameters<Widget>[0]): JSX.Element | null => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const table = context.model?.tables[property.id];
  const items = table?.items ?? [];
  if (items.length === 0) return null;
  const trackShape = context.model?.shapes?.nodeShapes.find((node) => node.targetClasses.includes(TRACK));
  const trackClass = trackShape ? pickLang(trackShape.label, context.language) : null;
  // The track an element lies on; own elements and their neighbours (the rows dview:objects names) each carry that column
  const model = context.model;
  const neighbourItems = settingList(property, 'objects').flatMap((key) => {
    const row = model?.rows[key];
    return (row && model?.tables[row.id]?.items) ?? [];
  });
  const trackOf = new Map(
    [...items, ...neighbourItems].map((item) => [item.iri, trackAmong(item.cells.owner)])
  );
  const own = new Set(items.map((item) => item.iri));
  const rows = items.map((item) => {
    const next = [...(item.cells['next-a'] ?? []), ...(item.cells['next-b'] ?? [])]
      .map((value) => value.value)
      .filter((iri, index, all) => !own.has(iri) && all.indexOf(iri) === index);
    return { iri: item.iri, before: next.filter((_, index) => index % 2 === 0), after: next.filter((_, index) => index % 2 === 1) };
  });
  // Each row is as tall as its larger side, so stacked neighbours never leave the drawing
  let top = 12;
  const placed = rows.map((row) => {
    const span = Math.max(1, row.before.length, row.after.length);
    const y = top + ((span - 1) * NODE_GAP) / 2 + 22;
    top += span * NODE_GAP + ROW_GAP;
    return { ...row, y };
  });
  const width = 760;
  const height = top;
  const open = (iri: string) => navigate(uriToDereferencePath(iri));
  const label = (iri: string) => {
    const track = trackOf.get(iri);
    const name = track ? recordName(track, context, trackClass) : t('entity.template.topology.element');
    return name.length > 24 ? `${name.slice(0, 23)}…` : name;
  };
  const node = (iri: string, x: number, y: number, mine: boolean) => (
    <g
      key={`${iri}-${x}-${y}`}
      className={mine ? styles.own : styles.next}
      role="link"
      tabIndex={0}
      aria-label={`${label(iri)} · ${elementName(iri)}`}
      onClick={() => open(iri)}
      onKeyDown={(event) => event.key === 'Enter' && open(iri)}
    >
      <title>{iri}</title>
      <rect x={x - 90} y={y - 17} width={180} height={34} rx={17} />
      <text x={x} y={y - 1} textAnchor="middle" className={styles.name}>
        {label(iri)}
      </text>
      <text x={x} y={y + 12} textAnchor="middle" className={styles.id}>
        {elementName(iri)}
      </text>
    </g>
  );
  const sideY = (y: number, count: number, i: number) => y + (i - (count - 1) / 2) * NODE_GAP;

  return (
    <div className={styles.topology}>
      <p className={styles.title}>{t('entity.template.topology.title', { count: rows.length })}</p>
      <div className={styles.stage}>
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="group" aria-label={t('entity.template.topology.aria', { count: rows.length })}>
          {placed.map((row) => (
            <g key={row.iri}>
              {row.before.map((iri, i) => (
                <line key={`l-${iri}`} x1={130 + 90} y1={sideY(row.y, row.before.length, i)} x2={380 - 90} y2={row.y} className={styles.edge} />
              ))}
              {row.after.map((iri, i) => (
                <line key={`r-${iri}`} x1={380 + 90} y1={row.y} x2={630 - 90} y2={sideY(row.y, row.after.length, i)} className={styles.edge} />
              ))}
              {row.before.map((iri, i) => node(iri, 130, sideY(row.y, row.before.length, i), false))}
              {node(row.iri, 380, row.y, true)}
              {row.after.map((iri, i) => node(iri, 630, sideY(row.y, row.after.length, i), false))}
            </g>
          ))}
        </svg>
      </div>
      <p className={styles.note}>{t('entity.template.topology.note')}</p>
    </div>
  );
};

export const TopologyViewer: Widget = (props) => <Topology {...props} />;
